import { NoBulletList, OrderedList } from 'components/Elements';
import { ActivityType, BuildMethod } from 'models/Activity';
import { Course } from 'models/Course';
import { LearningObjective } from 'models/LearningObjective';
import strings from 'strings/strings.json';

export type ReviewItem = {
    label: string;
    before: React.ReactNode;
    after: React.ReactNode;
};

export function addNewActivities(reviewItems: ReviewItem[], edit: Course) {
    [...edit.modules]
        .sort((a, b) => a.order - b.order)
        .forEach((draftModule) => {
            const newActivities = draftModule.activities.filter((x) => !x.id);
            newActivities.forEach((activity) => {
                reviewItems.push({
                    label: reviewItemLabelModuleActivity(draftModule.order, activity.order + 1, activity.type),
                    before: strings.reviewChangesModal.newChange,
                    after: (
                        <NoBulletList>
                            <li>
                                <b>{strings.reviewChangesModal.activity.titleLabel}:</b> {activity.title}
                            </li>
                            <li>
                                <b>{strings.reviewChangesModal.activity.descriptionLabel}:</b> {activity.description}
                            </li>
                            <li>
                                <b>{strings.reviewChangesModal.activity.durationLabel}:</b> {activity.durationInMins}{' '}
                                {strings.reviewChangesModal.activity.durationUnitLabel}
                            </li>
                            {activity.resources.map((x, i) => {
                                return (
                                    <li>
                                        <b>{`${strings.reviewChangesModal.activity.resourcesLabel} ${i + 1}:`}</b>{' '}
                                        {`Title: ${x.title}, Url: ${x.url}`}
                                    </li>
                                );
                            })}

                            {activity.type === ActivityType.TASK &&
                                activity.buildMethod === BuildMethod.TEXT_AND_IMAGE &&
                                activity.taskContents.map((taskContent, i) => {
                                    const prefix = `Task Content ${i + 1}`;
                                    switch (taskContent.type) {
                                        default:
                                        case 'text':
                                            return (
                                                <li>
                                                    <b>{`${prefix} Text:`}</b> {taskContent.text}
                                                </li>
                                            );
                                        case 'image':
                                            return (
                                                <>
                                                    <li>
                                                        <b>{`${prefix} Image:`}</b> {taskContent.image.filename}
                                                    </li>
                                                    <li>
                                                        <b>{`${prefix} Description:`}</b>{' '}
                                                        {taskContent.imageDescription}
                                                    </li>
                                                </>
                                            );
                                    }
                                })}
                            {activity.type === ActivityType.TASK &&
                                activity.buildMethod === BuildMethod.AUDIO &&
                                activity.audioDocument && (
                                    <li>
                                        <b>{strings.reviewChangesModal.activity.fileLabel}:</b>{' '}
                                        {activity.audioDocument.filename}
                                    </li>
                                )}
                            {activity.type === ActivityType.TASK &&
                                activity.buildMethod === BuildMethod.VIDEO &&
                                activity.videoDocument && (
                                    <li>
                                        <b>{strings.reviewChangesModal.activity.fileLabel}:</b>{' '}
                                        {activity.videoDocument.filename}
                                    </li>
                                )}

                            {activity.type === ActivityType.QUESTIONNAIRE &&
                                activity.questions.map((q) => (
                                    <>
                                        <li>
                                            <b>{strings.reviewChangesModal.activity.question}:</b> {q.question}
                                        </li>
                                        <li>
                                            <b>{strings.reviewChangesModal.activity.questionHint}:</b> {q.hint}
                                        </li>
                                    </>
                                ))}
                        </NoBulletList>
                    ),
                });
            });
        });
}

export function editActivities(reviewItems: ReviewItem[], snapshot: Course, edit: Course) {
    const editedModules = new Map(edit.modules.filter((x) => x.id).map((m) => [m.id, m]));

    [...snapshot.modules]
        .sort((a, b) => a.order - b.order)
        .forEach((originalModule) => {
            const editedModule = editedModules.get(originalModule.id);
            if (!editedModule) return; //impossible but return anyway

            // ACTIVITIES
            const snappedActivities = new Map(
                [...originalModule.activities].sort((a, b) => a.order - b.order).map((a) => [a.id, a]),
            );

            for (const after of editedModule.activities.filter((a) => a.id)) {
                //MODIFY
                const changes: { field: string; before: React.ReactNode; after: React.ReactNode }[] = [];

                const before = snappedActivities.get(after.id)!;

                if (after.title !== before.title) {
                    changes.push({ field: 'Title', before: before.title, after: after.title });
                }
                if (after.description !== before.description) {
                    changes.push({
                        field: 'Description',
                        before: before.description.length > 0 ? before.description : '—',
                        after: after.description,
                    });
                }
                if (after.durationInMins !== before.durationInMins) {
                    changes.push({
                        field: 'Duration',
                        before: before.durationInMins ? `${before.durationInMins} min` : '—',
                        after: after.durationInMins ? `${after.durationInMins} min` : '—',
                    });
                }
                if (after.type === ActivityType.PRESENTATION_SLIDE) {
                    if (after.content !== before.content) {
                        changes.push({
                            field: 'Text',
                            before: before.content ? before.content : '—',
                            after: after.content ? after.content : '—',
                        });
                    }
                }

                //RESOURCES
                const resourcesEdited = !(
                    before.resources.length === after.resources.length &&
                    before.resources.every(
                        (v, i) => v.title === after.resources[i].title || v.url === after.resources[i].url,
                    )
                );
                if (resourcesEdited) {
                    const length =
                        before.resources.length > after.resources.length
                            ? before.resources.length
                            : after.resources.length;
                    for (let index = 0; index < length; index++) {
                        const beforeResource =
                            index < before.resources.length
                                ? `${before.resources[index].title}, ${before.resources[index].url}`
                                : '—';
                        const afterResource =
                            index < after.resources.length
                                ? `${after.resources[index].title}, ${after.resources[index].url}`
                                : '—';
                        changes.push({
                            field: `Link ${index + 1}`,
                            before: beforeResource,
                            after: afterResource,
                        });
                    }
                }

                //TASK CONTENTS
                if (after.type === ActivityType.TASK && after.buildMethod === BuildMethod.TEXT_AND_IMAGE) {
                    //DELETED TASK CONTENTS
                    const afterTaskContentIds = new Set(after.taskContents.map((x) => x.id));
                    before.taskContents.forEach((x, i) => {
                        const deletedTaskContent = !afterTaskContentIds.has(x.id);
                        if (deletedTaskContent) {
                            changes.push({
                                field: `Task Content ${i + 1}`,
                                before: '—',
                                after: 'Delete',
                            });
                        }
                    });

                    after.taskContents.forEach((taskContent, index) => {
                        const beforeContent = before.taskContents[index];

                        let beforeContentString: string;
                        let afterContentString: string;

                        //BEFORE
                        if (beforeContent === undefined) {
                            beforeContentString = 'New';
                        } else {
                            if (
                                taskContent.type === 'text' &&
                                beforeContent.type === 'text' &&
                                taskContent.text === beforeContent.text
                            )
                                return;

                            if (
                                taskContent.type === 'image' &&
                                beforeContent.type === 'image' &&
                                taskContent.imageDescription === beforeContent.imageDescription
                            )
                                return;

                            if (beforeContent.type === 'text') {
                                beforeContentString = beforeContent.text;
                            } else {
                                beforeContentString = beforeContent.imageDescription;
                            }
                        }

                        //AFTER
                        if (taskContent.type === 'text') {
                            afterContentString = taskContent.text;
                        } else {
                            afterContentString = taskContent.imageDescription;
                        }

                        const prefix = `Content ${index + 1}`;

                        if (taskContent.type === 'image') {
                            changes.push({
                                field: `${prefix} Image`,
                                before: beforeContentString,
                                after: taskContent.image.filename,
                            });
                        }
                        changes.push({
                            field: taskContent.type === 'text' ? `${prefix} Text` : `${prefix} Image Description`,
                            before: beforeContentString,
                            after: afterContentString,
                        });
                    });
                }

                //IMAGES
                const originalImages = before.imageDocuments || [];
                const draftImages = after.imageDocuments || [];
                const draftImageMap = new Map(draftImages.map((img) => [img.id, img]));

                //DELETED OR EDITED IMAGES
                for (const originalImage of originalImages) {
                    const draftImage = draftImageMap.get(originalImage.id);
                    if (!draftImage) {
                        changes.push({
                            field: 'Image',
                            before: originalImage.filename,
                            after: strings.reviewChangesModal.deleteChange,
                        });
                    } else if (originalImage.filename !== draftImage.filename) {
                        changes.push({
                            field: 'Image',
                            before: originalImage.filename,
                            after: draftImage.filename,
                        });
                    }
                }

                //NEW IMAGES
                const originalImageIds = new Set(originalImages.map((img) => img.id));
                for (const draftImage of draftImages) {
                    if (!originalImageIds.has(draftImage.id)) {
                        changes.push({
                            field: 'Image',
                            before: '—',
                            after: draftImage.filename,
                        });
                    }
                }

                //VIDEO
                const originalVideo = before.videoDocument;
                const draftVideo = after.videoDocument;

                if (originalVideo && !draftVideo) {
                    changes.push({
                        field: 'Video',
                        before: originalVideo.filename,
                        after: strings.reviewChangesModal.deleteChange,
                    });
                } else if (!originalVideo && draftVideo) {
                    changes.push({
                        field: 'Video',
                        before: '—',
                        after: draftVideo.filename,
                    });
                } else if (originalVideo && draftVideo && originalVideo.filename !== draftVideo.filename) {
                    changes.push({
                        field: 'Video',
                        before: originalVideo.filename,
                        after: draftVideo.filename,
                    });
                }

                //AUDIO
                const originalAudio = before.audioDocument;
                const draftAudio = after.audioDocument;

                if (originalAudio && !draftAudio) {
                    changes.push({
                        field: 'Audio',
                        before: originalAudio.filename,
                        after: strings.reviewChangesModal.deleteChange,
                    });
                } else if (!originalAudio && draftAudio) {
                    changes.push({
                        field: 'Audio',
                        before: '—',
                        after: draftAudio.filename,
                    });
                } else if (originalAudio && draftAudio && originalAudio.filename !== draftAudio.filename) {
                    changes.push({
                        field: 'Audio',
                        before: originalAudio.filename,
                        after: draftAudio.filename,
                    });
                }

                // QUESTIONNAIRES
                if (before.questions && after.questions) {
                    const questionnaireChanges: {
                        field: string;
                        before: React.ReactNode;
                        after: React.ReactNode;
                    }[] = [];

                    const originalQuestions = before.questions;
                    const draftQuestions = after.questions;
                    const maxLength = Math.max(originalQuestions.length, draftQuestions.length);

                    for (let i = 0; i < maxLength; i++) {
                        const originalQuestion = originalQuestions[i];
                        const draftQuestion = draftQuestions[i];

                        if (originalQuestion && !draftQuestion) {
                            questionnaireChanges.push({
                                field: `Q${i + 1}`,
                                before:
                                    originalQuestion.question +
                                    (originalQuestion.hint ? ` (${originalQuestion.hint})` : ''),
                                after: strings.reviewChangesModal.deleteChange,
                            });
                        } else if (!originalQuestion && draftQuestion) {
                            questionnaireChanges.push({
                                field: `Q${i + 1}`,
                                before: '—',
                                after: draftQuestion.question + (draftQuestion.hint ? ` (${draftQuestion.hint})` : ''),
                            });
                        } else if (
                            originalQuestion &&
                            draftQuestion &&
                            (originalQuestion.question !== draftQuestion.question ||
                                originalQuestion.hint !== draftQuestion.hint)
                        ) {
                            questionnaireChanges.push({
                                field: `Q${i + 1}`,
                                before:
                                    originalQuestion.question +
                                    (originalQuestion.hint ? ` (${originalQuestion.hint})` : ''),
                                after: draftQuestion.question + (draftQuestion.hint ? ` (${draftQuestion.hint})` : ''),
                            });
                        }
                    }

                    if (questionnaireChanges.length > 0) {
                        changes.push({
                            field: 'Questionnaire',
                            before: (
                                <NoBulletList>
                                    {questionnaireChanges.map((change, index) => (
                                        <li key={index}>
                                            <b>{change.field}:</b> {change.before}
                                        </li>
                                    ))}
                                </NoBulletList>
                            ),
                            after: (
                                <NoBulletList>
                                    {questionnaireChanges.map((change, index) => (
                                        <li key={index}>
                                            <b>{change.field}:</b> {change.after}
                                        </li>
                                    ))}
                                </NoBulletList>
                            ),
                        });
                    }

                    if (changes.length > 0) {
                        reviewItems.push({
                            label: reviewItemLabelModuleActivity(originalModule.order, before.order + 1, before.type),
                            before: (
                                <NoBulletList>
                                    {changes.map((change, index) => (
                                        <li key={index}>
                                            <b>{change.field}:</b> {change.before}
                                        </li>
                                    ))}
                                </NoBulletList>
                            ),
                            after: (
                                <NoBulletList>
                                    {changes.map((change, index) => (
                                        <li key={index}>
                                            <b>{change.field}:</b> {change.after}
                                        </li>
                                    ))}
                                </NoBulletList>
                            ),
                        });
                    }
                }
            }
        });
}

export function editModuleTitleAndDescription(reviewItems: ReviewItem[], origin: Course, edit: Course) {
    const draftModules = new Map(edit.modules.map((m) => [m.id, m]));

    [...origin.modules]
        .sort((a, b) => a.order - b.order)
        .forEach((originModule) => {
            const draftModule = draftModules.get(originModule.id);
            if (!draftModule) return; //impossible but return anyway

            const moduleLevelChanges: { field: string; before: React.ReactNode; after: React.ReactNode }[] = [];

            if (draftModule.title !== originModule.title) {
                moduleLevelChanges.push({
                    field: strings.reviewChangesModal.moduleTitleLabel,
                    before: originModule.title,
                    after: draftModule.title,
                });
            }
            if (draftModule.description !== originModule.description) {
                moduleLevelChanges.push({
                    field: strings.reviewChangesModal.moduleDescriptionLabel,
                    before: originModule.description.length > 0 ? originModule.description : '—',
                    after: draftModule.description,
                });
            }
            if (moduleLevelChanges.length === 0) return;

            reviewItems.push({
                label: reviewItemLabelModule(originModule.order),
                before: (
                    <NoBulletList>
                        {moduleLevelChanges.map((moduleChange, index) => (
                            <li key={index}>
                                <b>{moduleChange.field}:</b> {moduleChange.before}
                            </li>
                        ))}
                    </NoBulletList>
                ),
                after: (
                    <NoBulletList>
                        {moduleLevelChanges.map((moduleChange, index) => (
                            <li key={index}>
                                <b>{moduleChange.field}:</b> {moduleChange.after}
                            </li>
                        ))}
                    </NoBulletList>
                ),
            });
        });
}

export function deleteModuleActivities(reviewItems: ReviewItem[], origin: Course, edit: Course) {
    const editModules = new Map(edit.modules.map((m) => [m.id, m]));

    [...origin.modules]
        .sort((a, b) => a.order - b.order)
        .forEach((originalModule) => {
            const editModule = editModules.get(originalModule.id);
            if (!editModule) return; //impossible but return anyway

            const draftActivityIds = new Set(editModule.activities.map((a) => a.id));
            const missingActivities = originalModule.activities.filter(
                (activity) => !draftActivityIds.has(activity.id),
            );

            missingActivities.forEach((activity) => {
                reviewItems.push({
                    label:
                        reviewItemLabelModuleActivity(editModule.order, activity.order + 1, activity.type) +
                        `\n${activity.title}`,
                    before: '—',
                    after: strings.reviewChangesModal.deleteChange,
                });
            });
        });
}

export function titleAndDescription(reviewItems: ReviewItem[], origin: Course, edit: Course) {
    if (edit.title !== origin.title) {
        reviewItems.push({
            label: strings.reviewChangesModal.courseTitleLabel,
            before: origin.title,
            after: edit.title,
        });
    }

    if (edit.description !== origin.description) {
        reviewItems.push({
            label: strings.reviewChangesModal.courseDescriptionLabel,
            before: origin.description.length > 0 ? origin.description : '—',
            after: edit.description,
        });
    }
}

export function learningObjectives(reviewItems: ReviewItem[], origin: Course, edit: Course) {
    const getDescriptions = (objectives: LearningObjective[]) =>
        [...objectives].sort((a, b) => a.order - b.order).map((o) => o.description);

    const originalObjectives = getDescriptions(origin.learningObjectives);
    const draftObjectives = getDescriptions(edit.learningObjectives);

    const hasChanges =
        originalObjectives.length !== draftObjectives.length ||
        originalObjectives.some((desc, i) => desc !== draftObjectives[i]);
    if (!hasChanges) return;

    reviewItems.push({
        label: strings.reviewChangesModal.learningObjectiveLabel,
        before: (
            <OrderedList>
                {originalObjectives.map((description, i) => (
                    <li key={i}>{description}</li>
                ))}
            </OrderedList>
        ),
        after: (
            <OrderedList>
                {draftObjectives.map((description, i) => (
                    <li key={i}>{description}</li>
                ))}
            </OrderedList>
        ),
    });
}

export function addNewModules(reviewItems: ReviewItem[], edit: Course) {
    const newModules = edit.modules.filter((m) => !m.id);
    newModules.forEach((module) => {
        reviewItems.push({
            label: reviewItemLabelModule(module.order),
            before: strings.reviewChangesModal.newChange,
            after: (
                <NoBulletList>
                    <li>
                        <b>{strings.reviewChangesModal.moduleTitleLabel}:</b> {module.title}
                    </li>
                    <li>
                        <b>{strings.reviewChangesModal.moduleDescriptionLabel}:</b> {module.description}
                    </li>
                </NoBulletList>
            ),
        });
    });
}

export function deleteModules(reviewItems: ReviewItem[], origin: Course, edit: Course) {
    const draftModules = new Map(edit.modules.map((m) => [m.id, m]));

    [...origin.modules]
        .sort((a, b) => a.order - b.order)
        .forEach((originalModule) => {
            if (!draftModules.has(originalModule.id)) {
                reviewItems.push({
                    label: reviewItemLabelModule(originalModule.order) + ` ${originalModule.title}`,
                    before: '—',
                    after: strings.reviewChangesModal.deleteChange,
                });
            }
        });
}

function reviewItemLabelModuleActivity(
    moduleNumber: number,
    activityNumber: number,
    activityType: ActivityType,
): string {
    return `${strings.reviewChangesModal.moduleLabel} ${moduleNumber}\n${strings.reviewChangesModal.activity.label} ${activityNumber} \n${activityType}`;
}

function reviewItemLabelModule(moduleNumber: number): string {
    return `${strings.reviewChangesModal.moduleLabel} ${moduleNumber}`;
}
